import React, { useRef, useState } from 'react';

import FormUploadEmpty from '/assets/icon/global/form-upload-empty.svg';
import FormUploadCancel from '/assets/icon/global/form-upload-cancel.svg';
import FileImageExcel from '/assets/icon/global/file-image-excel.svg';
import FileImagePdf from '/assets/icon/global/file-image-pdf.svg';
import { toLower } from 'lodash-es';
import { ptwApi } from '../../../api/ptw.api.ts';
import { v4 as uuidv4 } from 'uuid';

interface IProps {
  className?: string;
  style?: React.CSSProperties;
  files?: File[];
  serverFiles?: ServerFile[];
  onChange?: (e: any) => void;
  readOnly?: boolean;
  workId?: string;
  stateGubun?: string;
  onDownload?: (fileId: string, originalName: string) => void;
  acceptImages?: boolean;
  acceptVideo?: boolean;
}

interface ServerFile {
  fileId: string;
  fileOriginalname: string;
  size?: number;
  isNewFile?: boolean;
  file?: File;
}

const CustomUploader = (props: IProps) => {
  //const files = props.files ?? [];
  //const setFiles = props.setFiles ?? (() => {});
  const serverFiles = props.serverFiles ?? [];
  const [isDragging, setIsDragging] = useState(false);
  const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  const VIDEO_EXTS = ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm'];
  const BASE_EXTS = ['pdf', 'PDF', 'xls', 'xlsx'];
  const allowedExts = [
    ...BASE_EXTS,
    ...(props.acceptImages ? IMAGE_EXTS : []),
    ...(props.acceptVideo ? VIDEO_EXTS : [])
  ];
  //const [spcmFiles, setSpcmFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const workId = props.workId ?? 'issue';
  const stateGubun = props.stateGubun ?? 'init';
  const state = stateGubun == 'init' ? 'fileList' : 'newFileList';

  // 파일 용량계산
  const getTotalSize = () => {
    const serverSize = serverFiles.reduce((sum, f) => sum + (f.size ?? 0), 0);
    //const uploadSize = files.reduce((sum, f) => sum + f.size, 0);
    return serverSize + 0;
  };

  // 파일 중복 체크
  const isFile = (file: File, existingFiles: any[]) => {
    return existingFiles.some((f) => f.name === file.name && f.size === file.size);
  };

  // 파일 추가
  const addFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;

    const validFiles: File[] = [];
    let invalidExt = false;
    let duplicate = false;

    Array.from(newFiles).forEach((newFile) => {
      const ext = newFile.name.split('.').pop()?.toLowerCase();
      if (!ext || !allowedExts.map((e) => e.toLowerCase()).includes(ext)) {
        invalidExt = true;
        return;
      }

      // 중복 체크
      if (isFile(newFile, serverFiles)) {
        duplicate = true;
        return;
      }

      var file: any = {};
      file.fileId = uuidv4();
      file.fileOriginalname = newFile.name;
      file.size = newFile.size;
      file.isNewFile = true;
      file.file = newFile;

      validFiles.push(file as any);
    });

    if (invalidExt) {
      const types = ['PDF, Excel'];
      if (props.acceptImages) types.push('image');
      if (props.acceptVideo) types.push('video');
      alert(`Only ${types.join(', ')} files can be uploaded.`);
    }
    if (duplicate) alert('This file has already been attached.');
    if (validFiles.length > 0) {
      var files = [...serverFiles, ...validFiles];
      const e = {
        target: {
          type: 'file',
          value: files,
          name: state
        }
      };
      props.onChange?.(e);
    }
    if (fileInput.current) fileInput.current.value = ''; //동일 파일 다시 선택 가능
  };

  // 개별 삭제
  const removeFile = (fileId: string) => {
    if (!confirm('Do you want to delete the existing file?')) return;
    var files = props.serverFiles ?? [];
    var newFiles = [];
    for (var i in files) {
      if (files[i].fileId !== fileId) {
        newFiles.push(files[i]);
      }
    }
    const e = {
      target: {
        type: 'file',
        value: newFiles,
        name: state
      }
    };
    props.onChange?.(e);
    //setFiles(files.filter((_, i) => i !== index));
  };

  // 기존파일 삭제
  const removeServerFile = async (fileId: string) => {
    if (!confirm('Do you want to delete the existing file?')) return;
    try {
      //await ptwApi.deleteFile(fileId);
      //props.onRemoveServerFile?.(fileId);
    } catch (e) {
      alert('Failed to delete the file.');
      console.error(e);
    }
  };

  // 전체 삭제
  const removeAll = () => {
    //setFiles([]);
    console.log(props.serverFiles);
    const e = {
      target: {
        type: 'file',
        value: [],
        name: state
      }
    };
    props.onChange?.(e);
  };

  // drag 이벤트
  const onDragEnter = (e: React.DragEvent) => {
    if (props.readOnly) return;
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  // 파일 아이콘
  const getFileIcon = (file: File) => {
    if (file.name.endsWith('.pdf')) return FileImagePdf;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (IMAGE_EXTS.includes(ext)) return FileImageExcel;
    return FileImageExcel;
  };

  // 파일 용량
  const formatSize = (size: number) =>
    size < 1024 * 1024 ? `${(size / 1024).toFixed(1)} KB` : `${(size / 1024 / 1024).toFixed(1)} MB`;

  return (
    <div
      className={`custom-uploader ${props.className || ''} ${isDragging ? 'dragging' : ''}`}
      style={props.style}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="custom-uploader-header">
        {!props.readOnly && (
          <div className="all-cancel" onClick={removeAll}>
            <img src={FormUploadCancel} width={24} height={24} />
          </div>
        )}
        {props.readOnly && <div className="all-cancel" onClick={removeAll}></div>}
        <div className="file-name-volume">
          <span>File Name</span>
          <span> Volume ({formatSize(getTotalSize())}) </span>
        </div>
        {!props.readOnly && (
          <button type="button" className="add-file-btn" onClick={() => fileInput.current?.click()}>
            +Add File
          </button>
        )}
      </div>

      <div className="custom-uploader-body">
        <div className="file-item-list">
          {/* 기존 파일 */}
          {serverFiles.map((file) => (
            <div className="file-item server" key={file.fileId}>
              {!props.readOnly && (
                <div className="item-cancel" onClick={() => removeFile(file.fileId)}>
                  <img src={FormUploadCancel} width={24} height={24} />
                </div>
              )}
              {props.readOnly && (
                <div className="item-cancel" onClick={() => removeFile(file.fileId)}></div>
              )}
              <div className="item-value">
                <div className="item-file-value">
                  <img src={getFileIcon(new File([], file.fileOriginalname))} />
                  <span className="file-name">{file.fileOriginalname}</span>
                </div>
                <span>({formatSize(file.size ?? 0)})</span>
                {file.isNewFile ? (
                  <button></button>
                ) : (
                  <button
                    className="file-download-btn"
                    onClick={() =>
                      props.onDownload
                        ? props.onDownload(file.fileId, file.fileOriginalname)
                        : ptwApi.downloadFile(file.fileId, file.fileOriginalname, workId)
                    }
                  >
                    download
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* 신규 업로드 파일 */}
          {/*{files.map((file, index) => (*/}
          {/*  <div className="file-item" key={index}>*/}
          {/*    <div className="item-cancel" onClick={() => removeFile(index)}>*/}
          {/*      <img src={FormUploadCancel} width={24} height={24} />*/}
          {/*    </div>*/}
          {/*    <div className="item-value">*/}
          {/*      <div className="item-file-value">*/}
          {/*        <img src={getFileIcon(file)} />*/}
          {/*        <span className="file-name">{file.name}</span>*/}
          {/*      </div>*/}
          {/*      <span>({formatSize(file.size)})</span>*/}
          {/*    </div>*/}
          {/*  </div>*/}
          {/*))}*/}
        </div>

        {/* 비어있을 때 */}
        {serverFiles.length === 0 && (
          <div className="empty-body">
            <img src={FormUploadEmpty} />
            <span>
              {props.readOnly ? 'No attached files' : 'Please attach or drag files from my PC'}
            </span>
          </div>
        )}
      </div>

      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        accept={[
          '.pdf,.xls,.xlsx',
          props.acceptImages ? '.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg' : '',
          props.acceptVideo ? '.mp4,.avi,.mov,.wmv,.mkv,.webm' : ''
        ]
          .filter(Boolean)
          .join(',')}
        disabled={props.readOnly}
        onChange={(e) => addFiles(e.target.files)}
      />
    </div>
  );
};

export default CustomUploader;
